import { CEEDERO_API_ORIGIN } from "@/api/client";

import type { AccountProfile } from "@/account/hooks/use-account";
import type { TranslationKey } from "@/localization/hooks/use-translation";

/**
 * The account's picture: what may be uploaded, and where the current one is.
 *
 * These bounds are the API's and it enforces them; checking here turns a
 * round trip and a generic refusal into a sentence beside the control, which is
 * the difference between a user who knows their file is too big and one who
 * only knows it did not work.
 */

/** What the API will accept, and nothing else. */
export const AVATAR_MEDIA_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

export const MAXIMUM_AVATAR_BYTES = 2 * 1024 * 1024;

/** For the file picker, so the operating system's dialog offers only these. */
export const AVATAR_FILE_ACCEPT = AVATAR_MEDIA_TYPES.join(",");

export const validateAvatarFile = (file: File): TranslationKey | undefined => {
  if (!AVATAR_MEDIA_TYPES.includes(file.type as (typeof AVATAR_MEDIA_TYPES)[number])) {
    return "account.error.avatarType";
  }
  if (file.size > MAXIMUM_AVATAR_BYTES) {
    return "account.error.avatarTooLarge";
  }
  return undefined;
};

/**
 * Where the account's picture is, or nothing when it has none.
 *
 * A plain URL rather than bytes fetched and turned into an object URL: the
 * cookie is same-site, so the browser sends it on an image request, and the
 * response is already marked private and cacheable for five minutes.
 *
 * The revision is in the query string for exactly one reason — to defeat that
 * cache when the picture changes. Without it, uploading a new avatar leaves the
 * old one on screen until the cache expires, which reads as an upload that
 * silently failed.
 */
export const resolveAvatarUrl = (profile: AccountProfile | undefined) =>
  profile?.avatarRevision == null
    ? undefined
    : `${CEEDERO_API_ORIGIN}/v1/me/avatar?revision=${encodeURIComponent(profile.avatarRevision)}`;

/**
 * The letters shown when there is no picture.
 *
 * The display name first, because it is what the user chose to be called; the
 * username otherwise, because every account has one. Two initials at most: a
 * circle this size fits two, and three reads as a word.
 */
export const resolveAccountInitials = (profile: AccountProfile | undefined) => {
  const source = profile?.displayName?.trim() || profile?.username?.trim();
  if (!source) {
    return undefined;
  }
  const initials = source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => [...part][0]?.toLocaleUpperCase())
    .join("");
  return initials || undefined;
};
